import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useSAData } from './useSAData';
import { SAKpis, ProvisionalBanner } from './SAKpis';
import { Loading } from './Loading';
import { AnalyticsTable } from '@/components/po-grn/AnalyticsTable';
import { EXC_COLS, GROUP_COLS } from './columns';
import { inr } from '@/components/po-grn/format';
import { F } from './formulas';

const KPIS = [
  { key: 'margin', label: 'Current Margin Value (GST-incl. basis)', type: 'money', note: F.currentValue },
  { key: 'margin_pct', label: 'Current Margin %', type: 'pct', note: F.current },
  { key: 'margin_per_unit', label: 'Current Margin per Unit', type: 'money', note: 'Σ (SP − Inward) × Qty ÷ Σ Qty' },
  { key: 'sales', label: 'Total Sales Value', type: 'money' },
  { key: 'inward_value', label: 'Inward B2B Value (comparable qty)', type: 'money' },
  { key: 'neg_lines', label: 'Negative-Margin Lines', warn: true, drill: { patch: { flag: ['sales', 'negative_margin'] } } },
  { key: 'neg_value', label: 'Negative-Margin Value', type: 'money', drill: { patch: { flag: ['sales', 'negative_margin'] } } },
  { key: 'zero_margin_products', label: 'Products with Zero Margin', warn: true, drill: { patch: { flag: ['sales', 'zero_margin'] } } },
];
const DIMS = [['by_store', 'Store', 'franchise_ids'], ['by_product', 'Product', 'skus'], ['by_warehouse', 'Warehouse', 'warehouses'],
  ['by_creator', 'Creator', 'created_by'], ['by_month', 'Month', 'month'], ['by_unit', 'Unit of Measure', 'units']];
const NEG_COLS = EXC_COLS.filter((c) => ['type', 'severity', 'source_document', 'doc_date', 'franchise_name', 'product_id', 'product_name',
  'batch_number', 'selling_price', 'inward', 'quantity', 'margin_amount', 'margin_pct', 'impact', 'description'].includes(c.key));

export const OltownsTab = ({ params, onDrill }) => {
  const { data, loading } = useSAData('/oltowns-margin', params);
  const [dim, setDim] = useState('by_store');
  const [, label, fkey] = DIMS.find((d) => d[0] === dim);
  const v = data?.validation;
  return (
    <div className="space-y-4 relative">
      <Loading on={loading} />
      <ProvisionalBanner k={data && { margin_confirmed: data.kpis.confirmed, unresolved_lines: data.kpis.unresolved_lines, margin_coverage_pct: data.kpis.coverage_pct }} />
      <SAKpis items={KPIS} data={data?.kpis} onDrill={onDrill} testPrefix="sa-oltowns" />
      <div className="rounded-xl border border-orange-200/70 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[#3b2416] mr-2">Current Margin by</span>
          {DIMS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setDim(k)} data-testid={`sa-oltowns-dim-${k}`}
              className={`px-3 py-1 text-xs rounded-full border ${dim === k ? 'bg-[#3b2416] text-white border-[#3b2416]' : 'border-orange-200 text-stone-600 hover:bg-orange-50'}`}>{l}</button>
          ))}
        </div>
        <AnalyticsTable columns={GROUP_COLS(label)} rows={data?.[dim] || []} testId="sa-oltowns-group-table" rowKey={(r) => r.key} />
        <div className="text-[11px] text-stone-500">Tip: use the global filters to narrow a {label.toLowerCase()}; click Transactions to see lines.
          {fkey && data?.[dim]?.[0] && (
            <button type="button" className="ml-2 underline text-orange-700" data-testid="sa-oltowns-drill-top"
              onClick={() => onDrill({ patch: { [fkey]: fkey === 'month' ? data[dim][0].key : [data[dim][0].key], flag: ['sales'] } }, `${label} ${data[dim][0].name}`)}>
              Drill into top {label.toLowerCase()}
            </button>
          )}
        </div>
      </div>
      {v && (
        <div className="rounded-xl border border-orange-200/70 bg-white p-4" data-testid="sa-oltowns-validation">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#3b2416] mb-2"><ShieldCheck size={15} className="text-orange-600" />Margin Data-Quality Validation</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
            {[['Source (reported) margin total', inr(v.reported_total)], ['Calculated (SP − Inward) × Qty', inr(v.calculated_total)],
              ['Mismatch lines', v.mismatch_lines], ['Missing inward price lines', v.missing_inward_lines],
              ['Missing reported margin lines', v.missing_margin_lines], ['Unconfirmed pricing lines', v.unconfirmed_lines]].map(([l, x]) => (
              <div key={l} className="rounded-lg bg-orange-50/60 px-3 py-2"><div className="text-stone-500">{l}</div><div className="font-bold text-[#3b2416] tabular-nums">{x}</div></div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="text-sm font-semibold text-[#3b2416] mb-2">Negative & Below-Target Current Margin Exceptions</div>
        <AnalyticsTable columns={NEG_COLS} rows={data?.negative_lines || []} testId="sa-oltowns-negative-table" rowKey={(r) => r.exception_id} />
      </div>
    </div>
  );
};
